import * as React from "react";
import {useState} from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import {POST} from "APIclient";
import properties from "constants/ApplicationConstants";

const globalCSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');
  @keyframes slideUp {
    from { opacity: 0; transform: translateY(24px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes toastIn {
    from { opacity: 0; transform: translateX(40px); }
    to   { opacity: 1; transform: translateX(0); }
  }
  .aml-page * { box-sizing: border-box; }
  .aml-page { font-family: 'DM Sans', 'Segoe UI', sans-serif; }

  /* MUI DatePicker overrides */
  .aml-datepicker .MuiInputBase-root {
    font-family: 'DM Sans', sans-serif !important;
    font-size: 0.91rem !important;
    border-radius: 10px !important;
    background: #f8fafc !important;
    border: 1.5px solid #e2e8f0 !important;
    padding: 2px 8px !important;
  }
  .aml-datepicker-readonly .MuiInputBase-root {
    font-family: 'DM Sans', sans-serif !important;
    font-size: 0.91rem !important;
    border-radius: 10px !important;
    background: #f1f5f9 !important;
    border: 1.5px solid #e2e8f0 !important;
    padding: 2px 8px !important;
    color: #718096 !important;
    pointer-events: none;
  }
  .aml-datepicker .MuiOutlinedInput-notchedOutline,
  .aml-datepicker-readonly .MuiOutlinedInput-notchedOutline { border: none !important; }
  .aml-datepicker .MuiInputBase-root:focus-within {
    border-color: #3b55e6 !important;
    box-shadow: 0 0 0 3px rgba(59,85,230,0.10) !important;
    background: #fff !important;
  }
  .aml-datepicker .MuiInputLabel-root,
  .aml-datepicker-readonly .MuiInputLabel-root {
    font-family: 'DM Sans', sans-serif !important;
    font-size: 0.85rem !important;
  }
  .aml-datepicker .MuiFormHelperText-root {
    font-family: 'DM Sans', sans-serif !important;
    font-size: 0.72rem !important;
  }
  .aml-datepicker-readonly .MuiSvgIcon-root { display: none; }

  .aml-btn-primary:not(:disabled):hover { transform: translateY(-1px); }
  .aml-btn-primary:disabled { opacity: 0.45; cursor: not-allowed; }

  /* ── Responsive layout ── */
  .aml-page-wrap {
    padding: 10px;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    min-height: 100%;
  }
  @media (max-width: 479px) {
    .aml-page-wrap { padding: 6px !important; }
  }

  .aml-card {
    border-radius: 20px;
    overflow: hidden;
    box-shadow: 0 24px 64px rgba(67,97,238,0.10), 0 4px 16px rgba(0,0,0,0.06);
    background: #fff;
    width: 100%;
    max-width: 620px;
    animation: slideUp 0.45s cubic-bezier(.22,1,.36,1) both;
  }
  @media (max-width: 479px) {
    .aml-card { border-radius: 14px; }
  }

  .aml-header-inner { padding: 14px 22px 12px; }
  @media (max-width: 479px) {
    .aml-header-inner { padding: 12px 16px 10px; }
  }

  .aml-body-inner { padding: 16px 22px 20px; }
  @media (max-width: 479px) {
    .aml-body-inner { padding: 12px 14px 16px; }
  }

  /* Three-column date row → stacks on mobile */
  .aml-date-row {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 10px;
    margin-bottom: 12px;
  }
  @media (max-width: 599px) {
    .aml-date-row { grid-template-columns: 1fr; }
  }
  @media (min-width: 600px) and (max-width: 767px) {
    .aml-date-row { grid-template-columns: 1fr 1fr; }
  }

  /* Summary pill row */
  .aml-summary-row {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    margin-bottom: 4px;
  }

  /* Toast */
  .aml-toast {
    position: fixed;
    top: 24px;
    right: 24px;
    z-index: 9999;
    padding: 14px 20px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 0.87rem;
    font-weight: 600;
    font-family: inherit;
    box-shadow: 0 8px 32px rgba(0,0,0,0.14);
    animation: toastIn 0.35s cubic-bezier(.22,1,.36,1) both;
    max-width: 340px;
  }
  @media (max-width: 479px) {
    .aml-toast {
      top: auto !important;
      bottom: 16px;
      right: 12px;
      left: 12px;
      max-width: 100%;
      font-size: 0.82rem;
    }
  }
`;

const s = {
  page: {
    background: "linear-gradient(135deg, #f0f4ff 0%, #fafbff 60%, #eef2ff 100%)",
    fontFamily: "'DM Sans', 'Segoe UI', sans-serif"
  },
  header: {
    background: "linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)",
    position: "relative",
    overflow: "hidden"
  },
  hCircle1: {
    position: "absolute",
    width: 130,
    height: 130,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.08)",
    top: -40,
    right: -30,
    pointerEvents: "none"
  },
  hCircle2: {
    position: "absolute",
    width: 60,
    height: 60,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.06)",
    bottom: -15,
    left: 15,
    pointerEvents: "none"
  },
  headerIcon: {
    width: 34,
    height: 34,
    borderRadius: 10,
    background: "rgba(255,255,255,0.18)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 7,
    backdropFilter: "blur(4px)"
  },
  headerTitle: {
    color: "#fff",
    fontSize: "1.1rem",
    fontWeight: 700,
    margin: 0,
    letterSpacing: "-0.3px",
    fontFamily: properties.fontFamily
  },
  headerSub: {
    color: "rgba(255,255,255,0.75)",
    fontFamily: properties.fontFamily,
    fontSize: "0.76rem",
    marginTop: 2,
    fontWeight: 400
  },

  sectionLabel: {
    fontSize: "0.70rem",
    fontWeight: 700,
    color: "#718096",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: 8
  },

  fieldGroup: {marginBottom: 12},
  label: {
    display: "block",
    fontSize: "0.75rem",
    fontWeight: 600,
    color: "#4a5568",
    marginBottom: 5,
    letterSpacing: "0.4px",
    textTransform: "uppercase"
  },
  labelMuted: {
    display: "block",
    fontSize: "0.75rem",
    fontWeight: 600,
    color: "#a0aec0",
    marginBottom: 5,
    letterSpacing: "0.4px",
    textTransform: "uppercase"
  },
  required: {color: "#e53e3e", marginLeft: 2},

  selectWrap: {position: "relative"},
  select: {
    width: "100%",
    padding: "9px 36px 9px 12px",
    borderRadius: 10,
    border: "1.5px solid #e2e8f0",
    background: "#f8fafc",
    fontSize: "0.88rem",
    color: "#2d3748",
    outline: "none",
    fontFamily: "inherit",
    cursor: "pointer",
    appearance: "none",
    WebkitAppearance: "none",
    transition: "border-color 0.2s, box-shadow 0.2s"
  },
  selectFocus: {
    borderColor: "#7c3aed",
    background: "#fff",
    boxShadow: "0 0 0 3px rgba(124,58,237,0.10)"
  },
  selectArrow: {
    position: "absolute",
    right: 12,
    top: "50%",
    transform: "translateY(-50%)",
    pointerEvents: "none",
    color: "#a0aec0"
  },

  textarea: {
    width: "100%",
    padding: "9px 12px",
    borderRadius: 10,
    border: "1.5px solid #e2e8f0",
    background: "#f8fafc",
    fontSize: "0.88rem",
    color: "#2d3748",
    outline: "none",
    fontFamily: "inherit",
    resize: "vertical",
    minHeight: 62,
    transition: "border-color 0.2s, box-shadow 0.2s"
  },
  textareaError: {borderColor: "#fc8181", background: "#fff5f5"},
  textareaFocus: {
    borderColor: "#7c3aed",
    background: "#fff",
    boxShadow: "0 0 0 3px rgba(124,58,237,0.10)"
  },

  errorMsg: {
    color: "#e53e3e",
    fontSize: "0.72rem",
    marginTop: 4,
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontWeight: 500
  },

  divider: {height: 1, background: "#edf2f7", margin: "14px 0"},

  summaryPill: color => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "5px 12px",
    borderRadius: 20,
    fontSize: "0.78rem",
    fontWeight: 600,
    background: color.bg,
    color: color.text,
    border: `1.5px solid ${color.border}`
  }),

  readonlyBox: {
    width: "100%",
    padding: "9px 12px",
    borderRadius: 10,
    border: "1.5px solid #e2e8f0",
    background: "#f1f5f9",
    fontSize: "0.88rem",
    color: "#718096",
    fontFamily: "inherit",
    cursor: "not-allowed",
    display: "flex",
    alignItems: "center",
    gap: 6
  },

  btnPrimary: {
    width: "100%",
    padding: "10px 28px",
    borderRadius: 10,
    border: "none",
    background: "linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)",
    color: "#fff",
    fontWeight: 700,
    fontSize: "0.88rem",
    cursor: "pointer",
    fontFamily: "inherit",
    transition: "opacity 0.15s, transform 0.15s",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    boxShadow: "0 4px 16px rgba(124,58,237,0.28)",
    marginTop: 14
  },

  toastSuccess: {background: "#f0fff4", color: "#276749", border: "1.5px solid #9ae6b4"},
  toastError: {background: "#fff5f5", color: "#c53030", border: "1.5px solid #feb2b2"}
};

const DURATION_MAP = {
  1: {days: 30, label: "~1 month"},
  2: {days: 61, label: "~2 months"},
  3: {days: 91, label: "~3 months"},
  4: {days: 121, label: "~4 months"},
  5: {days: 152, label: "~5 months"},
  6: {days: 182, label: "~6 months"}
};

function FieldError({msg}) {
  if (!msg) return null;
  return (
    <div style={s.errorMsg}>
      <svg width="12" height="12" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
          clipRule="evenodd"
        />
      </svg>
      {msg}
    </div>
  );
}

export default function ApplyMaternityLeaves() {
  const empId = Number(window.sessionStorage.getItem("empId"));

  const [startDate, setStartDate] = useState(dayjs());
  const [endDate, setEndDate] = useState(dayjs().add(182, "days"));
  const [selectedMonths, setSelectedMonths] = useState(6);
  const [selectedDays, setSelectedDays] = useState(182);
  const [reason, setReason] = useState("");
  const [errors, setErrors] = useState({startDate: "", reason: ""});
  const [toast, setToast] = useState({open: false, color: "", message: ""});
  const [reasonFocused, setReasonFocused] = useState(false);
  const [durationFocused, setDurationFocused] = useState(false);

  React.useEffect(() => {
    if (toast.open) {
      const t = setTimeout(() => setToast(p => ({...p, open: false})), 3000);
      return () => clearTimeout(t);
    }
  }, [toast.open]);

  const recalcEnd = (start, months) => {
    const days = DURATION_MAP[months]?.days ?? months * 30;
    setSelectedDays(days);
    setEndDate(start.add(days, "days"));
  };

  const handleStartDateChange = newDate => {
    setStartDate(newDate);
    recalcEnd(newDate, selectedMonths);
    if (!dayjs(newDate).isValid()) {
      setErrors(p => ({...p, startDate: "Valid start date is required"}));
    } else {
      setErrors(p => ({...p, startDate: ""}));
    }
  };

  const handleMonthsChange = e => {
    const months = Number(e.target.value);
    setSelectedMonths(months);
    recalcEnd(startDate, months);
  };

  const handleReasonChange = e => {
    const val = e.target.value;
    setReason(val);
    if (!val.trim()) {
      setErrors(p => ({...p, reason: "Reason is required"}));
    } else if (/\d/.test(val)) {
      setErrors(p => ({...p, reason: "Reason should not contain numbers"}));
    } else {
      setErrors(p => ({...p, reason: ""}));
    }
  };

  const isButtonDisabled = !reason.trim() || !!errors.reason || !!errors.startDate;

  const handleSubmit = async e => {
    e.preventDefault();
    if (!empId) {
      setToast({open: true, color: "error", message: "Employee ID missing. Please log in."});
      return;
    }
    if (!reason.trim()) {
      setErrors(p => ({...p, reason: "Reason is required"}));
      return;
    }

    const payload = {
      employee: {empId},
      startDate: dayjs(startDate).format("YYYY-MM-DD"),
      no_of_weeks: String(selectedDays),
      endDate: dayjs(endDate).format("YYYY-MM-DD"),
      reason
    };

    try {
      const response = await POST("employee/maternity", payload);
      if (response.status === 200) {
        setToast({
          open: true,
          color: "success",
          message: response.data.message || "Leave request submitted successfully."
        });
        setReason("");
        setErrors({startDate: "", reason: ""});
      }
    } catch (error) {
      const msg =
        error.response?.status === 400 ||
        error.response?.status === 401 ||
        error.response?.status === 403
          ? error.response.data
          : "An error occurred while submitting the leave request.";
      setToast({open: true, color: "error", message: msg});
      setReason("");
    }
  };

  const pillColors = {
    purple: {bg: "#f5f3ff", text: "#6d28d9", border: "#ddd6fe"},
    blue: {bg: "#ebf8ff", text: "#2b6cb0", border: "#bee3f8"},
    green: {bg: "#f0fff4", text: "#276749", border: "#9ae6b4"}
  };

  return (
    <DashboardLayout>
      <style>{globalCSS}</style>
      <DashboardNavbar />

      <div className="aml-page aml-page-wrap" style={s.page}>
        <div className="aml-card">
          <div className="aml-header-inner" style={s.header}>
            <div style={s.hCircle1} />
            <div style={s.hCircle2} />
            <div style={s.headerIcon}>
              <svg
                width="20"
                height="20"
                fill="none"
                stroke="#fff"
                strokeWidth="2.2"
                viewBox="0 0 24 24"
              >
                <path d="M12 21.593c-5.63-5.539-11-10.297-11-14.402C1 4.144 2.611 2 5.067 2c1.695 0 3.35 1.093 4.492 2.594L12 7l2.441-2.406C15.582 3.093 17.237 2 18.933 2 21.389 2 23 4.144 23 7.191c0 4.105-5.37 8.863-11 14.402z" />
              </svg>
            </div>
            <p style={s.headerTitle}>Apply Maternity Leave</p>
            <p style={s.headerSub}>Submit your maternity leave request — up to 6 months</p>
          </div>

          <div className="aml-body-inner">
            <div className="aml-summary-row" style={{marginBottom: 14}}>
              <span style={s.summaryPill(pillColors.purple)}>
                <svg width="12" height="12" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                    clipRule="evenodd"
                  />
                </svg>
                {selectedMonths} {selectedMonths === 1 ? "Month" : "Months"}
              </span>
              <span style={s.summaryPill(pillColors.blue)}>
                <svg width="12" height="12" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                    clipRule="evenodd"
                  />
                </svg>
                {selectedDays} Days
              </span>
              <span style={s.summaryPill(pillColors.green)}>
                <svg width="12" height="12" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                Ends {endDate.format("DD MMM YYYY")}
              </span>
            </div>

            <div style={s.divider} />

            <p style={s.sectionLabel}>Select Duration</p>
            <div className="aml-date-row">
              <div>
                <label style={s.label}>
                  Start Date<span style={s.required}>*</span>
                </label>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <div className="aml-datepicker">
                    <DatePicker
                      format="DD/MM/YYYY"
                      value={startDate}
                      onChange={handleStartDateChange}
                      slotProps={{
                        textField: {
                          size: "small",
                          fullWidth: true,
                          error: !!errors.startDate,
                          helperText: errors.startDate,
                          sx: {
                            "& .MuiInputBase-root": {
                              borderRadius: "10px",
                              fontSize: "0.91rem",
                              fontFamily: "'DM Sans', sans-serif"
                            }
                          }
                        }
                      }}
                    />
                  </div>
                </LocalizationProvider>
              </div>

              <div>
                <label style={s.label}>
                  Duration<span style={s.required}>*</span>
                </label>
                <div style={s.selectWrap}>
                  <select
                    value={selectedMonths}
                    onChange={handleMonthsChange}
                    onFocus={() => setDurationFocused(true)}
                    onBlur={() => setDurationFocused(false)}
                    style={{
                      ...s.select,
                      ...(durationFocused ? s.selectFocus : {})
                    }}
                  >
                    {[1, 2, 3, 4, 5, 6].map(m => (
                      <option key={m} value={m}>
                        {m} {m === 1 ? "Month" : "Months"} ({DURATION_MAP[m].days} days)
                      </option>
                    ))}
                  </select>
                  <span style={s.selectArrow}>
                    <svg
                      width="14"
                      height="14"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </span>
                </div>
              </div>

              <div>
                <label style={s.labelMuted}>End Date (auto)</label>
                <div style={s.readonlyBox}>
                  <svg
                    width="14"
                    height="14"
                    fill="none"
                    stroke="#a0aec0"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                  {endDate.format("DD/MM/YYYY")}
                </div>
              </div>
            </div>

            <div style={s.divider} />

            <div style={s.fieldGroup}>
              <label style={s.label}>
                Reason<span style={s.required}>*</span>
              </label>
              <textarea
                value={reason}
                onChange={handleReasonChange}
                onFocus={() => setReasonFocused(true)}
                onBlur={() => setReasonFocused(false)}
                placeholder="Reason for maternity leave..."
                style={{
                  ...s.textarea,
                  ...(errors.reason ? s.textareaError : {}),
                  ...(reasonFocused && !errors.reason ? s.textareaFocus : {})
                }}
              />
              <FieldError msg={errors.reason} />
            </div>

            <button
              className="aml-btn-primary"
              style={s.btnPrimary}
              onClick={handleSubmit}
              disabled={isButtonDisabled}
            >
              <svg
                width="16"
                height="16"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                viewBox="0 0 24 24"
              >
                <path d="M22 2L11 13" />
                <path d="M22 2L15 22 11 13 2 9l20-7z" />
              </svg>
              Submit Maternity Leave
            </button>
          </div>
        </div>
      </div>

      {toast.open && (
        <div
          className="aml-toast"
          style={toast.color === "success" ? s.toastSuccess : s.toastError}
        >
          {toast.color === "success" ? (
            <svg
              width="18"
              height="18"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              viewBox="0 0 24 24"
            >
              <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          ) : (
            <svg
              width="18"
              height="18"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              viewBox="0 0 24 24"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
          )}
          {toast.message}
        </div>
      )}
    </DashboardLayout>
  );
}
